import React, { Component } from 'react';

import styled from 'styled-components';
import colorString from 'color-string';

import ManagedComponent from '../components/managed-component';
import iOSInstalledCheck from '../icons/ios-installed-check.svg';
import androidInstalledCheck from '../icons/android-installed-check.svg';
import { isThemeiOS } from '../themes';
import { SectionChild } from '../components/section';

const FlexContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-grow: 1;
  justify-content: ${({centered = false}) => centered ? 'center' : 'flex-start' };
`;

const ColorBase = styled.div`
  border-radius: 27px;
  height: 44px;
  width: 44px;
`;

const ColorCircle = ColorBase.withComponent('button').extend`
  background-color: ${({color}) => color};
  border: 0;
  font-size: 20px;
  padding: 0;
  position: relative;
  width: 100%;
  box-shadow: inset 0 0 20px 0px rgba(0, 0, 0, 0.1);

  &:after {
    content: "";
    display: block;
    width: 100%;
    height: 0;
    padding-bottom: 100%;
  }
`;

const ColorContainer = ColorBase.extend`
  border: 3px solid transparent;
  padding: 2px;
  margin: auto 0;
  display: inline-flex;
  ${({ shouldFlex}) => shouldFlex && `flex: 1 0 12%`};
`;

const CheckedIcon = styled.div`
  top: 50%;
  left: 50%;
  margin-top: -10px;
  margin-left: -12px;
  position: absolute;
  height: 26px;
  width: 26px;
  > svg {
    height: 20px;
    fill: ${({ isDark }) => isDark ? 'white' : 'black'};
  }
`;

/*
 * Valid CSS colors include Hexadecimal values, RGB(A) colors, HSL(A) colors,
 * and any predefined color names. For more information and examples, see
 * https://www.w3schools.com/cssref/css_colors_legal.asp
 */
const getValidColor = (color) => {
  if(!color || typeof color !== "string") {
    throw new Error('ColorSelect: colors must be a list of {color: string}');
  }

  const parsedColor = colorString.get(color.trim().toLowerCase());

  if (parsedColor === null) {
    throw new Error(`ColorSelect: ${color} is not a valid css color`);
  }

  return colorString.to.hex(parsedColor.value);
};

const isDark = (color) => {
  const [red, green, blue] = colorString.get.rgb(color);

  // Referenced here: https://24ways.org/2010/calculating-color-contrast/
  const luma = (red * 299 + green * 587 + blue * 114) / 1000;

  // Arbitrary value close to pure white luma
  return luma < 235;
};

class ColorSelect extends Component {
  static _type = 'ColorSelect';
  _type = 'ColorSelect';

  get colors() {
    const { colors } = this.props;

    return colors.map(({color, value = color}) => {
      return {
        color: getValidColor(color),
        value: value
      };
    });
  }

  handleSelect = ({ currentTarget: { value } }) => {
    const { value: oldValue, onSelection } = this.props;

    if (typeof onSelection === 'function') {
      onSelection(value, oldValue);
    }
  };

  renderColorCircles() {
    return (
      this.colors.map(({color, value = color}) => {
        const selected = value === this.props.value;
        const shouldFlex = this.colors.length < 6;
        return (
          <ColorContainer key={value} color={color} value={value} selected={selected} shouldFlex={shouldFlex}>
            <ColorCircle color={color} value={value} onClick={this.handleSelect}>
              {selected && 
                <CheckedIcon
                  isDark={isDark(color)}
                  dangerouslySetInnerHTML={{
                    __html:
                      isThemeiOS() ? iOSInstalledCheck : androidInstalledCheck
                  }}
                />
              }
            </ColorCircle>
          </ColorContainer>
        );
      })
    )
  }

  render() {
    const { title, description } = this.props;

    return (
      <SectionChild title={title} description={description}>
        {this.colors &&
          <FlexContainer centered={this.props.centered}>
            {this.renderColorCircles()}
          </FlexContainer>
        }
      </SectionChild>
    );
  }
}

export default class extends Component {
  parseValue(rawValue) {
    try {
      return JSON.parse(rawValue);
    } catch (err) {
      return rawValue;
    }
  }

  render() {
    const {
      title,
      description,
      centered,
      colors,
      value,
      onSelection,
      settingsKey
    } = this.props;

    const colorProps = {
      title,
      description,
      centered,
      colors,
      value,
      onSelection
    };

    if (!settingsKey) {
      return (
        <ColorSelect {...colorProps} />
      );
    }

    return (
      <ManagedComponent settingsKey={settingsKey}>
        {({ value, onChange }) => {
          const parsedValue = this.parseValue(value);
          return (
            <ColorSelect
              {...colorProps}
              value={parsedValue}
              onSelection={(value, oldValue) => {
                if (typeof onSelection === 'function') {
                  onSelection(value, oldValue);
                }
                onChange(JSON.stringify(value));
              }}
            />
          );
        }}
      </ManagedComponent>
    );
  }
}



// WEBPACK FOOTER //
// ./src/fitbit-components/color-select.js